import { useEffect, useRef } from 'react';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { analyticsManager } from 'features/analytics';
import {
  recommendationAnalyticsProduct,
  RecommendationListingLike,
} from 'features/analytics/utils/recommendationAnalytics';

interface UseRecommendationsViewEventProps {
  /** The recommended listings on screen, in the order shown. */
  readonly listings: readonly RecommendationListingLike[];
  /** True once real recommendations are rendered, not skeletons or the featured fallback. */
  readonly isShown: boolean;
}

/**
 * Fires `view_recommendations` once per set of recommendations a screen renders, with the listings
 * as products. The screen's page type says which surface it was. First-party only: hotel analytics
 * (Adobe, GA4, Tealium) don't receive it, so their dashboards are unchanged.
 */
export const useRecommendationsViewEvent = ({
  listings,
  isShown,
}: UseRecommendationsViewEventProps) => {
  const sentFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isShown || listings.length === 0) {
      return;
    }

    const key = listings.map((listing) => listing.id).join(',');
    if (sentFor.current === key) {
      return;
    }
    sentFor.current = key;

    analyticsManager.sendEvent(
      ANALYTICS_EVENT.VIEW_RECOMMENDATIONS,
      {
        [ANALYTICS_PROPERTY.Products]: JSON.stringify(
          listings.map((listing, index) =>
            recommendationAnalyticsProduct(listing, index),
          ),
        ),
      },
      { providers: [] },
    );
  }, [isShown, listings]);
};
